import React, { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import AdminShell from '../components/admin/AdminShell';
import { LIGHT_MAP_STYLE } from '../components/booking/mapStyles';
import {
  completeAdminDriverOnboardingPersonalDocs,
  completeAdminDriverOnboardingVehicleInfo,
  lookupAdminDriverOnboardingByEmail,
  sendAdminDriverPasswordSetup,
  startAdminDriverOnboarding,
  submitAdminDriverOnboardingForReview,
  updateAdminDriverOnboardingAccountType,
  updateAdminDriverOnboardingBankDetails,
  updateAdminDriverOnboardingCompanyDetails,
  updateAdminDriverOnboardingOperatingTown,
  updateAdminDriverOnboardingPhone,
  updateAdminDriverOnboardingVehicleDetails,
  uploadAdminDriverOnboardingFaceLiveness,
  uploadAdminDriverOnboardingFile,
} from '../services/api/admin';
import { ensureGooglePlacesLoaded } from '../services/googlePlaces';
import type {
  AdminDriverBankDetailsPayload,
  AdminDriverCompanyDetailsPayload,
  AdminDriverOperatingTownPayload,
  AdminDriverReviewDetail,
  AdminDriverVehicleDetailsPayload,
} from '../types/admin';
import type { DriverAttachment } from '../types/driverAuth';
import '../styles/register.css';
import '../styles/admin.css';

type Driver = AdminDriverReviewDetail['driver'];
type LoadingAction = string | null;

interface DateFields {
  issuedOn: string;
  validFrom: string;
  validTo: string;
}

interface DriverTownPrediction {
  description: string;
  placeId: string;
  primaryText: string;
  secondaryText: string;
}

interface GoogleAddressComponent {
  long_name?: string;
  types?: string[];
}

const blankDates: DateFields = {
  issuedOn: '',
  validFrom: '',
  validTo: '',
};

const personalDocs = [
  { type: 'ID_COPY', label: 'ID copy', hint: 'Admin may skip this and let the driver upload it later.' },
  { type: 'CRIMINAL_RECORD_CHECK', label: 'Criminal record', hint: 'Optional for admin-assisted capture if the driver will upload it.' },
  { type: 'DRIVER_PHOTO', label: 'Driver photo', hint: 'Profile photo if available.' },
];

const vehicleDocs = [
  { type: 'VEHICLE_PHOTO_FRONT', label: 'Vehicle front photo' },
  { type: 'VEHICLE_PHOTO_LEFT', label: 'Vehicle left photo' },
  { type: 'VEHICLE_PHOTO_RIGHT', label: 'Vehicle right photo' },
  { type: 'VEHICLE_PHOTO_BACK', label: 'Vehicle back photo' },
  { type: 'ROADWORTHY', label: 'Roadworthy certificate' },
  { type: 'LICENCE_DISK', label: 'Licence disk' },
  { type: 'PDP', label: 'PDP' },
  { type: 'INSURANCE_LETTER', label: 'Insurance letter' },
  { type: 'BANK_LETTER', label: 'Bank letter' },
];

const faceChallenges = [
  { challengeType: 'STRAIGHT' as const, sequenceOrder: 1 as const, label: 'Straight selfie' },
  { challengeType: 'LEFT' as const, sequenceOrder: 2 as const, label: 'Left selfie' },
  { challengeType: 'RIGHT' as const, sequenceOrder: 3 as const, label: 'Right selfie' },
];

function firstVehicle(driver: Driver | null) {
  return driver?.vehicles?.[0] ?? null;
}

function attachmentLabel(attachment?: DriverAttachment | null): string {
  if (!attachment) return 'Not uploaded';
  return attachment.originalFileName || attachment.fileName || attachment.fileType || 'Uploaded';
}

function getAttachment(driver: Driver | null, type: string): DriverAttachment | null {
  return driver?.attachments?.[type] ?? null;
}

function cleanText(value: string): string | undefined {
  const trimmed = value.trim();
  return trimmed || undefined;
}

function parseOptionalNumber(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = Number.parseFloat(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
}

function extractTownFromPlace(place: any): string | null {
  const components = (place.address_components ?? []) as GoogleAddressComponent[];
  const find = (type: string) =>
    components.find((component: GoogleAddressComponent) => component.types?.includes(type))?.long_name;

  return (
    find('locality') ||
    find('sublocality') ||
    find('administrative_area_level_2') ||
    place.name ||
    null
  );
}

function extractProvinceFromPlace(place: any): string | null {
  const components = (place.address_components ?? []) as GoogleAddressComponent[];
  return (
    components.find((component: GoogleAddressComponent) => component.types?.includes('administrative_area_level_1'))
      ?.long_name || null
  );
}

function parseCoordinate(value: string): number | null {
  const parsed = parseOptionalNumber(value);
  return parsed == null ? null : parsed;
}

function fileDatesForSubmission(dates: DateFields) {
  return {
    issuedOn: cleanText(dates.issuedOn),
    validFrom: cleanText(dates.validFrom),
    validTo: cleanText(dates.validTo),
  };
}

function getInitialDates(driver: Driver | null): Record<string, DateFields> {
  const allTypes = [...personalDocs, ...vehicleDocs].map((doc) => doc.type);
  return Object.fromEntries(
    allTypes.map((type) => {
      const attachment = getAttachment(driver, type);
      return [
        type,
        {
          issuedOn: attachment?.issuedOn ?? '',
          validFrom: attachment?.validFrom ?? '',
          validTo: attachment?.validTo ?? '',
        },
      ];
    }),
  );
}

const UploadedPill: React.FC<{ uploaded: boolean }> = ({ uploaded }) => (
  <span className={`admin-pill ${uploaded ? 'admin-pill-success' : 'admin-pill-warning'}`}>
    {uploaded ? 'Uploaded' : 'Can skip'}
  </span>
);

const AssistAccordion: React.FC<{
  title: string;
  copy: string;
  badge?: string;
  defaultOpen?: boolean;
  actions?: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, copy, badge, defaultOpen = false, actions, children }) => (
  <details className="admin-assist-accordion" open={defaultOpen}>
    <summary className="admin-assist-accordion-summary">
      <span className="admin-assist-accordion-copy">
        {badge ? <span className="admin-pill admin-pill-active">{badge}</span> : null}
        <strong>{title}</strong>
        <small>{copy}</small>
      </span>
      <span className="admin-assist-accordion-controls">
        {actions}
        <span className="admin-assist-accordion-chevron" aria-hidden="true">v</span>
      </span>
    </summary>
    <div className="admin-assist-accordion-body">{children}</div>
  </details>
);

const AdminTownMap: React.FC<{
  lat: number | null;
  lng: number | null;
  disabled: boolean;
  onPick: (lat: number, lng: number) => void;
}> = ({ lat, lng, disabled, onPick }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<any>(null);
  const markerRef = useRef<any>(null);
  const listenerRef = useRef<any>(null);
  const [mapError, setMapError] = useState<string | null>(null);

  useEffect(() => {
    if (lat == null || lng == null || !containerRef.current) return undefined;

    let cancelled = false;
    ensureGooglePlacesLoaded()
      .then(() => {
        if (cancelled || !containerRef.current) return;
        const google = (window as any).google;
        if (!google?.maps?.Map) {
          setMapError('Map is unavailable right now.');
          return;
        }

        const position = { lat, lng };
        const map = new google.maps.Map(containerRef.current, {
          center: position,
          disableDefaultUI: true,
          gestureHandling: disabled ? 'none' : 'greedy',
          styles: LIGHT_MAP_STYLE,
          zoom: 12,
          zoomControl: true,
          zoomControlOptions: {
            position: google.maps.ControlPosition.RIGHT_TOP,
          },
        });
        const marker = new google.maps.Marker({
          map,
          position,
        });

        listenerRef.current = map.addListener('click', (event: any) => {
          if (disabled || !event.latLng) return;
          onPick(event.latLng.lat(), event.latLng.lng());
        });
        mapRef.current = map;
        markerRef.current = marker;
        setMapError(null);
      })
      .catch((error: Error) => {
        if (!cancelled) setMapError(error.message || 'Map is unavailable right now.');
      });

    return () => {
      cancelled = true;
      listenerRef.current?.remove?.();
      markerRef.current?.setMap?.(null);
      mapRef.current = null;
      markerRef.current = null;
      listenerRef.current = null;
    };
  }, [disabled, lat, lng, onPick]);

  useEffect(() => {
    if (lat == null || lng == null || !mapRef.current || !markerRef.current) return;
    const position = { lat, lng };
    mapRef.current.panTo(position);
    markerRef.current.setPosition(position);
  }, [lat, lng]);

  if (lat == null || lng == null) {
    return (
      <div className="admin-assist-map-placeholder">
        Select a town result to preview its map and fill coordinates.
      </div>
    );
  }

  return (
    <div className="admin-assist-map-wrap">
      <div className="admin-assist-map" ref={containerRef} role="img" aria-label="Selected driver operating town map" />
      {mapError ? <p className="register-field-error">{mapError}</p> : null}
      <p className="register-field-hint">Click the map to adjust the saved latitude and longitude.</p>
    </div>
  );
};

const UploadSlot: React.FC<{
  label: string;
  hint?: string;
  attachment?: DriverAttachment | null;
  disabled: boolean;
  accept?: string;
  onUpload: (file: File) => void;
  children?: React.ReactNode;
}> = ({ label, hint, attachment, disabled, accept = 'image/*,.pdf', onUpload, children }) => (
  <div className="admin-assist-upload-card">
    <div className="admin-assist-upload-header">
      <div>
        <strong>{label}</strong>
        <span>{attachmentLabel(attachment)}</span>
      </div>
      <UploadedPill uploaded={Boolean(attachment)} />
    </div>
    {hint ? <p className="admin-panel-copy">{hint}</p> : null}
    {children}
    <label className="register-file-picker">
      <span className="register-file-placeholder">Choose file</span>
      <span className="admin-assist-upload-action">Upload</span>
      <input
        accept={accept}
        className="register-file-input"
        disabled={disabled}
        type="file"
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = '';
          if (file) onUpload(file);
        }}
      />
    </label>
  </div>
);

const AdminDriverOnboardingAssistPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [driver, setDriver] = useState<Driver | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [loadingAction, setLoadingAction] = useState<LoadingAction>(null);
  const [townSearchQuery, setTownSearchQuery] = useState('');
  const [townPredictions, setTownPredictions] = useState<DriverTownPrediction[]>([]);
  const [townSearchLoading, setTownSearchLoading] = useState(false);

  const [townForm, setTownForm] = useState({
    homeBaseTown: '',
    homeBaseProvince: '',
    homeBaseCountry: 'South Africa',
    homeBaseLat: '',
    homeBaseLng: '',
  });
  const [phoneE164, setPhoneE164] = useState('');
  const [vehicleForm, setVehicleForm] = useState({
    vehicle_type: 'BAKKIE',
    registration_number: '',
    make: '',
    model: '',
    year: String(new Date().getFullYear()),
    vehicle_color: '',
    insurance_business_use_confirmed: false,
  });
  const [accountType, setAccountType] = useState<'INDIVIDUAL_OWNER' | 'COMPANY_DRIVER'>('INDIVIDUAL_OWNER');
  const [companyForm, setCompanyForm] = useState({
    companyName: '',
    companyEmail: '',
    companyPhone1E164: '',
    companyPhone2E164: '',
    companyRegNumber: '',
  });
  const [bankForm, setBankForm] = useState({
    bankName: '',
    accountNumber: '',
    branchCode: '',
    accountHolderName: '',
  });
  const [fileDates, setFileDates] = useState<Record<string, DateFields>>(getInitialDates(null));

  const currentVehicle = useMemo(() => firstVehicle(driver), [driver]);
  const driverLoaded = Boolean(driver?.id);
  const busy = Boolean(loadingAction);
  const selectedLat = parseCoordinate(townForm.homeBaseLat);
  const selectedLng = parseCoordinate(townForm.homeBaseLng);

  useEffect(() => {
    if (!townSearchQuery.trim()) {
      setTownPredictions([]);
      return undefined;
    }

    let active = true;
    const timeoutId = window.setTimeout(async () => {
      try {
        await ensureGooglePlacesLoaded();
        const googleWindow = window as any;
        const autocompleteService = new googleWindow.google.maps.places.AutocompleteService();
        setTownSearchLoading(true);
        autocompleteService.getPlacePredictions(
          {
            componentRestrictions: { country: 'za' },
            input: townSearchQuery.trim(),
            types: ['(cities)'],
          },
          (predictions: any[] | null, status: string) => {
            if (!active) return;

            setTownSearchLoading(false);

            if (status !== googleWindow.google.maps.places.PlacesServiceStatus.OK || !predictions) {
              setTownPredictions([]);
              return;
            }

            setTownPredictions(
              predictions.map((prediction) => ({
                description: prediction.description,
                placeId: prediction.place_id,
                primaryText: prediction.structured_formatting?.main_text || prediction.description,
                secondaryText: prediction.structured_formatting?.secondary_text || '',
              })),
            );
          },
        );
      } catch (placesError) {
        if (!active) return;
        setTownSearchLoading(false);
        setTownPredictions([]);
        setError(placesError instanceof Error ? placesError.message : 'Could not load town search.');
      }
    }, 250);

    return () => {
      active = false;
      window.clearTimeout(timeoutId);
    };
  }, [townSearchQuery]);

  useEffect(() => {
    if (!driver) return;

    setEmail(driver.email ?? '');
    setName(driver.name ?? '');
    setPhoneE164(driver.phoneE164 ?? '');
    setTownForm({
      homeBaseTown: driver.homeBaseTown ?? '',
      homeBaseProvince: driver.homeBaseProvince ?? '',
      homeBaseCountry: driver.homeBaseCountry ?? 'South Africa',
      homeBaseLat: driver.homeBaseLat == null ? '' : String(driver.homeBaseLat),
      homeBaseLng: driver.homeBaseLng == null ? '' : String(driver.homeBaseLng),
    });

    const vehicle = firstVehicle(driver);
    setVehicleForm({
      vehicle_type: vehicle?.vehicleType ?? 'BAKKIE',
      registration_number: vehicle?.registrationNumber ?? '',
      make: vehicle?.make ?? '',
      model: vehicle?.model ?? '',
      year: vehicle?.year == null ? String(new Date().getFullYear()) : String(vehicle.year),
      vehicle_color: vehicle?.vehicleColor ?? '',
      insurance_business_use_confirmed: Boolean(vehicle?.insuranceBusinessUseConfirmed),
    });

    setAccountType((driver.accountType as 'INDIVIDUAL_OWNER' | 'COMPANY_DRIVER') ?? 'INDIVIDUAL_OWNER');
    setCompanyForm({
      companyName: driver.companyDetails?.companyName ?? '',
      companyEmail: driver.companyDetails?.companyEmail ?? '',
      companyPhone1E164: driver.companyDetails?.companyPhone1E164 ?? '',
      companyPhone2E164: driver.companyDetails?.companyPhone2E164 ?? '',
      companyRegNumber: driver.companyDetails?.companyRegNumber ?? '',
    });
    setBankForm({
      bankName: driver.bankDetails?.bankName ?? '',
      accountNumber: driver.bankDetails?.accountNumber ?? '',
      branchCode: driver.bankDetails?.branchCode ?? '',
      accountHolderName:
        driver.bankDetails?.accountHolderName ?? driver.bankDetails?.accountHolder ?? driver.name ?? '',
    });
    setFileDates(getInitialDates(driver));
  }, [driver]);

  async function runDriverAction(label: string, action: () => Promise<Driver>) {
    setLoadingAction(label);
    setError(null);
    setSuccess(null);
    try {
      const updatedDriver = await action();
      setDriver(updatedDriver);
      setSuccess(`${label} saved.`);
    } catch (actionError) {
      setError(actionError instanceof Error ? actionError.message : 'Something went wrong.');
    } finally {
      setLoadingAction(null);
    }
  }

  async function runMessageAction(label: string, action: () => Promise<string>) {
    setLoadingAction(label);
    setError(null);
    setSuccess(null);
    try {
      const message = await action();
      setSuccess(message);
    } catch (actionError) {
      setError(actionError instanceof Error ? actionError.message : 'Something went wrong.');
    } finally {
      setLoadingAction(null);
    }
  }

  function requireDriverId(): string | null {
    if (!driver?.id) {
      setError('Find or create a driver profile first.');
      return null;
    }
    return driver.id;
  }

  function updateDates(type: string, key: keyof DateFields, value: string) {
    setFileDates((current) => ({
      ...current,
      [type]: {
        ...(current[type] ?? blankDates),
        [key]: value,
      },
    }));
  }

  const updateMapCoordinates = useCallback((lat: number, lng: number) => {
    setTownForm((form) => ({
      ...form,
      homeBaseLat: lat.toFixed(6),
      homeBaseLng: lng.toFixed(6),
    }));
  }, []);

  async function handleTownSelection(prediction: DriverTownPrediction) {
    setTownSearchLoading(true);
    setError(null);
    try {
      await ensureGooglePlacesLoaded();
      const googleWindow = window as any;
      const placesService = new googleWindow.google.maps.places.PlacesService(document.createElement('div'));

      placesService.getDetails(
        {
          fields: ['address_components', 'formatted_address', 'geometry', 'name'],
          placeId: prediction.placeId,
        },
        (place: any, status: string) => {
          setTownSearchLoading(false);

          if (status !== googleWindow.google.maps.places.PlacesServiceStatus.OK || !place) {
            setError('Could not load the selected town details. Please try another result.');
            return;
          }

          const town = extractTownFromPlace(place);
          const province = extractProvinceFromPlace(place) || townForm.homeBaseProvince;
          const lat = place.geometry?.location?.lat?.();
          const lng = place.geometry?.location?.lng?.();

          if (!town || typeof lat !== 'number' || typeof lng !== 'number') {
            setError('This result is missing location coordinates. Please pick another town.');
            return;
          }

          setTownForm((form) => ({
            ...form,
            homeBaseTown: town,
            homeBaseProvince: province,
            homeBaseCountry: 'South Africa',
            homeBaseLat: lat.toFixed(6),
            homeBaseLng: lng.toFixed(6),
          }));
          setTownSearchQuery(place.formatted_address || prediction.description);
          setTownPredictions([]);
        },
      );
    } catch (selectionError) {
      setTownSearchLoading(false);
      setError(selectionError instanceof Error ? selectionError.message : 'Could not load the selected town details.');
    }
  }

  function handleLookup(event: FormEvent) {
    event.preventDefault();
    if (!email.trim()) {
      setError('Enter the driver email address first.');
      return;
    }
    void runDriverAction('Driver lookup', () => lookupAdminDriverOnboardingByEmail(email));
  }

  function handleStart() {
    if (!email.trim()) {
      setError('Enter the driver email address first.');
      return;
    }
    void runDriverAction('Assisted onboarding profile', () =>
      startAdminDriverOnboarding({
        email: email.trim(),
        name: cleanText(name),
        homeBaseTown: cleanText(townForm.homeBaseTown),
        homeBaseProvince: cleanText(townForm.homeBaseProvince),
        homeBaseCountry: cleanText(townForm.homeBaseCountry),
        homeBaseLat: parseOptionalNumber(townForm.homeBaseLat),
        homeBaseLng: parseOptionalNumber(townForm.homeBaseLng),
      }),
    );
  }

  function handleTownSave(event: FormEvent) {
    event.preventDefault();
    const driverId = requireDriverId();
    if (!driverId) return;

    const payload: AdminDriverOperatingTownPayload = {
      homeBaseTown: townForm.homeBaseTown.trim(),
      homeBaseProvince: cleanText(townForm.homeBaseProvince),
      homeBaseCountry: cleanText(townForm.homeBaseCountry),
      homeBaseLat: parseOptionalNumber(townForm.homeBaseLat),
      homeBaseLng: parseOptionalNumber(townForm.homeBaseLng),
    };
    void runDriverAction('Operating town', () => updateAdminDriverOnboardingOperatingTown(driverId, payload));
  }

  function handleVehicleSave(event: FormEvent) {
    event.preventDefault();
    const driverId = requireDriverId();
    if (!driverId) return;

    const parsedYear = Number.parseInt(vehicleForm.year, 10);
    const payload: AdminDriverVehicleDetailsPayload = {
      vehicle_type: vehicleForm.vehicle_type,
      registration_number: vehicleForm.registration_number.trim(),
      make: vehicleForm.make.trim(),
      model: vehicleForm.model.trim(),
      year: Number.isFinite(parsedYear) ? parsedYear : new Date().getFullYear(),
      vehicle_color: vehicleForm.vehicle_color.trim(),
      insurance_business_use_confirmed: vehicleForm.insurance_business_use_confirmed,
    };
    void runDriverAction('Vehicle details', () => updateAdminDriverOnboardingVehicleDetails(driverId, payload));
  }

  function handleCompanySave(event: FormEvent) {
    event.preventDefault();
    const driverId = requireDriverId();
    if (!driverId) return;

    const payload: AdminDriverCompanyDetailsPayload = {
      companyName: companyForm.companyName.trim(),
      companyEmail: companyForm.companyEmail.trim(),
      companyPhone1E164: companyForm.companyPhone1E164.trim(),
      companyPhone2E164: cleanText(companyForm.companyPhone2E164),
      companyRegNumber: cleanText(companyForm.companyRegNumber),
    };
    void runDriverAction('Company details', () => updateAdminDriverOnboardingCompanyDetails(driverId, payload));
  }

  function handleBankSave(event: FormEvent) {
    event.preventDefault();
    const driverId = requireDriverId();
    if (!driverId) return;

    const payload: AdminDriverBankDetailsPayload = {
      bankName: bankForm.bankName.trim(),
      accountNumber: bankForm.accountNumber.trim(),
      branchCode: cleanText(bankForm.branchCode),
      accountHolderName: bankForm.accountHolderName.trim(),
    };
    void runDriverAction('Bank details', () => updateAdminDriverOnboardingBankDetails(driverId, payload));
  }

  function uploadDocument(type: string, file: File) {
    const driverId = requireDriverId();
    if (!driverId) return;

    void runDriverAction(type.replaceAll('_', ' '), () =>
      uploadAdminDriverOnboardingFile(driverId, type, file, fileDatesForSubmission(fileDates[type] ?? blankDates)),
    );
  }

  return (
    <AdminShell
      badge="Driver onboarding"
      copy="Create or resume a driver profile from the admin console, upload files supplied by the driver, and submit the profile for the usual approval flow."
      title="Assisted driver onboarding"
    >
      <div className="admin-assist-layout">
        <section className="admin-panel">
          <div className="admin-panel-header">
            <div>
              <h2 className="admin-panel-title">Find or create</h2>
              <p className="admin-panel-copy">
                Use the email address the driver gave support. If the driver already started onboarding, this resumes that profile.
              </p>
            </div>
          </div>
          <form className="register-field-group" onSubmit={handleLookup}>
            <div className="register-field-inline">
              <label className="register-field">
                <span className="register-label">Email</span>
                <input
                  className="register-input"
                  inputMode="email"
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                />
              </label>
              <label className="register-field">
                <span className="register-label">Driver name</span>
                <input
                  className="register-input"
                  type="text"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                />
              </label>
            </div>
            <div className="admin-assist-action-row">
              <button className="register-secondary-button" disabled={busy} type="submit">
                {loadingAction === 'Driver lookup' ? 'Searching...' : 'Search'}
              </button>
              <button className="register-secondary-button" disabled={busy} type="button" onClick={handleStart}>
                {loadingAction === 'Assisted onboarding profile' ? 'Starting...' : 'Start or resume'}
              </button>
            </div>
          </form>
          {error ? <p className="register-field-error">{error}</p> : null}
          {success ? <p className="register-success-banner">{success}</p> : null}
        </section>

        <aside className="admin-panel">
          <div className="admin-panel-header">
            <div>
              <h2 className="admin-panel-title">Current driver</h2>
              <p className="admin-panel-copy">Live snapshot after the last saved action.</p>
            </div>
          </div>
          {driver ? (
            <div className="admin-detail-grid">
              <div className="admin-detail-card">
                <span>Name</span>
                <strong>{driver.name || 'Not set'}</strong>
              </div>
              <div className="admin-detail-card">
                <span>Stage</span>
                <strong>{driver.onboardingsStep || 'Not started'}</strong>
              </div>
              <div className="admin-detail-card admin-detail-card-span-2">
                <span>Email</span>
                <strong>{driver.email}</strong>
              </div>
              <div className="admin-detail-card">
                <span>Phone</span>
                <strong>{driver.phoneE164 || 'Not set'}</strong>
              </div>
              <div className="admin-detail-card">
                <span>Face liveness</span>
                <strong>{driver.faceLiveness ? 'Complete' : 'Pending'}</strong>
              </div>
            </div>
          ) : (
            <p className="admin-empty">No driver loaded yet.</p>
          )}
        </aside>
      </div>

      <AssistAccordion
        badge="Basics"
        copy="Search a town, confirm the map position, and save the driver's contact number."
        defaultOpen
        title="Location and phone"
      >
        <div className="admin-assist-section-grid">
          <form className="register-field-group" onSubmit={handleTownSave}>
            <label className="register-field admin-assist-town-search">
              <span className="register-label">Town search</span>
              <input
                className="register-input"
                placeholder="Search a South African town"
                type="search"
                value={townSearchQuery}
                onChange={(event) => setTownSearchQuery(event.target.value)}
              />
              <div className="admin-assist-town-results">
                {townSearchLoading ? <div className="register-town-empty">Loading towns...</div> : null}
                {!townSearchLoading && !townPredictions.length && townSearchQuery.trim() ? (
                  <div className="register-town-empty">No matching towns yet.</div>
                ) : null}
                {townPredictions.map((prediction) => (
                  <button
                    className="register-town-result"
                    key={prediction.placeId}
                    onClick={() => void handleTownSelection(prediction)}
                    type="button"
                  >
                    <strong>{prediction.primaryText}</strong>
                    <span>{prediction.secondaryText || prediction.description}</span>
                  </button>
                ))}
              </div>
            </label>
            <div className="admin-assist-location-grid">
              <div className="register-field-group">
                <div className="register-field-inline">
                  <label className="register-field">
                    <span className="register-label">Town</span>
                    <input
                      className="register-input"
                      value={townForm.homeBaseTown}
                      onChange={(event) => setTownForm((form) => ({ ...form, homeBaseTown: event.target.value }))}
                    />
                  </label>
                  <label className="register-field">
                    <span className="register-label">Province</span>
                    <input
                      className="register-input"
                      value={townForm.homeBaseProvince}
                      onChange={(event) => setTownForm((form) => ({ ...form, homeBaseProvince: event.target.value }))}
                    />
                  </label>
                </div>
                <div className="register-field-inline">
                  <label className="register-field">
                    <span className="register-label">Country</span>
                    <input
                      className="register-input"
                      value={townForm.homeBaseCountry}
                      onChange={(event) => setTownForm((form) => ({ ...form, homeBaseCountry: event.target.value }))}
                    />
                  </label>
                  <label className="register-field">
                    <span className="register-label">Latitude</span>
                    <input
                      className="register-input"
                      inputMode="decimal"
                      value={townForm.homeBaseLat}
                      onChange={(event) => setTownForm((form) => ({ ...form, homeBaseLat: event.target.value }))}
                    />
                  </label>
                </div>
                <label className="register-field">
                  <span className="register-label">Longitude</span>
                  <input
                    className="register-input"
                    inputMode="decimal"
                    value={townForm.homeBaseLng}
                    onChange={(event) => setTownForm((form) => ({ ...form, homeBaseLng: event.target.value }))}
                  />
                </label>
                <button className="register-secondary-button" disabled={busy || !driverLoaded} type="submit">
                  Save location
                </button>
              </div>
              <AdminTownMap
                disabled={busy}
                lat={selectedLat}
                lng={selectedLng}
                onPick={updateMapCoordinates}
              />
            </div>
          </form>
          <form className="register-field-group" onSubmit={(event) => {
            event.preventDefault();
            const driverId = requireDriverId();
            if (driverId) {
              void runDriverAction('Phone number', () => updateAdminDriverOnboardingPhone(driverId, phoneE164));
            }
          }}>
            <label className="register-field">
              <span className="register-label">Phone E.164</span>
              <input
                className="register-input"
                inputMode="tel"
                placeholder="+27821234567"
                value={phoneE164}
                onChange={(event) => setPhoneE164(event.target.value)}
              />
            </label>
            <p className="admin-panel-copy">This stores the number for the driver. It does not mark the phone as OTP verified.</p>
            <button className="register-secondary-button" disabled={busy || !driverLoaded} type="submit">
              Save phone
            </button>
          </form>
        </div>
      </AssistAccordion>

      <AssistAccordion
        badge={driver?.faceLiveness ? 'Done' : 'Photos'}
        copy="Upload selfies supplied by the driver. Admin-assisted onboarding does not open a camera."
        title="Face liveness"
      >
        <div className="admin-assist-upload-grid">
          {faceChallenges.map((challenge) => (
            <UploadSlot
              key={challenge.challengeType}
              accept="image/*"
              attachment={getAttachment(driver, `FACE_LIVENESS_${challenge.challengeType}`)}
              disabled={busy || !driverLoaded}
              label={challenge.label}
              onUpload={(file) => {
                const driverId = requireDriverId();
                if (!driverId) return;
                void runDriverAction(challenge.label, () =>
                  uploadAdminDriverOnboardingFaceLiveness(
                    driverId,
                    challenge.challengeType,
                    challenge.sequenceOrder,
                    file,
                  ),
                );
              }}
            />
          ))}
        </div>
      </AssistAccordion>

      <AssistAccordion
        actions={(
          <button
            className="admin-assist-summary-button"
            disabled={busy || !driverLoaded}
            type="button"
            onClick={(event) => {
              event.preventDefault();
              const driverId = requireDriverId();
              if (driverId) void runDriverAction('Personal documents step', () => completeAdminDriverOnboardingPersonalDocs(driverId));
            }}
          >
            Mark complete
          </button>
        )}
        badge="Docs"
        copy="Upload what support has received. Sensitive files can be skipped and the driver can add them later in the app profile."
        title="Personal documents"
      >
        <div className="admin-assist-upload-grid">
          {personalDocs.map((doc) => (
            <UploadSlot
              key={doc.type}
              attachment={getAttachment(driver, doc.type)}
              disabled={busy || !driverLoaded}
              hint={doc.hint}
              label={doc.label}
              onUpload={(file) => uploadDocument(doc.type, file)}
            >
              <label className="register-field">
                <span className="register-label">Issued on</span>
                <input
                  className="register-input"
                  type="date"
                  value={fileDates[doc.type]?.issuedOn ?? ''}
                  onChange={(event) => updateDates(doc.type, 'issuedOn', event.target.value)}
                />
              </label>
            </UploadSlot>
          ))}
        </div>
      </AssistAccordion>

      <AssistAccordion
        actions={(
          <button
            className="admin-assist-summary-button"
            disabled={busy || !driverLoaded}
            type="button"
            onClick={(event) => {
              event.preventDefault();
              const driverId = requireDriverId();
              if (driverId) void runDriverAction('Vehicle information step', () => completeAdminDriverOnboardingVehicleInfo(driverId));
            }}
          >
            Mark complete
          </button>
        )}
        badge={currentVehicle?.registrationNumber || 'Vehicle'}
        copy="Capture the primary vehicle and upload any photos or compliance documents already supplied."
        title="Vehicle details and files"
      >
        <form className="register-field-group" onSubmit={handleVehicleSave}>
          <div className="register-field-inline">
            <label className="register-field">
              <span className="register-label">Vehicle type</span>
              <select
                className="register-input"
                value={vehicleForm.vehicle_type}
                onChange={(event) => setVehicleForm((form) => ({ ...form, vehicle_type: event.target.value }))}
              >
                <option value="BAKKIE">Bakkie</option>
                <option value="PANEL_VAN">Panel van</option>
                <option value="TRUCK_4_TON">4 ton truck</option>
                <option value="MEDIUM_TRUCK">Medium truck</option>
                <option value="TRUCK_14_TON">14 ton truck</option>
                <option value="SEMI_TRUCK">Semi truck</option>
                <option value="HORSE_AND_TRAILER">Horse and trailer</option>
              </select>
            </label>
            <label className="register-field">
              <span className="register-label">Registration</span>
              <input
                className="register-input"
                value={vehicleForm.registration_number}
                onChange={(event) => setVehicleForm((form) => ({ ...form, registration_number: event.target.value }))}
              />
            </label>
          </div>
          <div className="register-field-inline">
            <label className="register-field">
              <span className="register-label">Make</span>
              <input
                className="register-input"
                value={vehicleForm.make}
                onChange={(event) => setVehicleForm((form) => ({ ...form, make: event.target.value }))}
              />
            </label>
            <label className="register-field">
              <span className="register-label">Model</span>
              <input
                className="register-input"
                value={vehicleForm.model}
                onChange={(event) => setVehicleForm((form) => ({ ...form, model: event.target.value }))}
              />
            </label>
          </div>
          <div className="register-field-inline">
            <label className="register-field">
              <span className="register-label">Year</span>
              <input
                className="register-input"
                inputMode="numeric"
                value={vehicleForm.year}
                onChange={(event) => setVehicleForm((form) => ({ ...form, year: event.target.value }))}
              />
            </label>
            <label className="register-field">
              <span className="register-label">Colour</span>
              <input
                className="register-input"
                value={vehicleForm.vehicle_color}
                onChange={(event) => setVehicleForm((form) => ({ ...form, vehicle_color: event.target.value }))}
              />
            </label>
          </div>
          <label className="register-checkbox-row">
            <input
              checked={vehicleForm.insurance_business_use_confirmed}
              type="checkbox"
              onChange={(event) =>
                setVehicleForm((form) => ({ ...form, insurance_business_use_confirmed: event.target.checked }))
              }
            />
            Insurance permits business use
          </label>
          <div className="admin-assist-action-row">
            <button className="register-secondary-button" disabled={busy || !driverLoaded} type="submit">
              Save vehicle
            </button>
            <span className="admin-panel-copy">
              Current vehicle: {currentVehicle?.registrationNumber || 'none'}
            </span>
          </div>
        </form>

        <div className="admin-assist-upload-grid admin-assist-nested-block">
          {vehicleDocs.map((doc) => (
            <UploadSlot
              key={doc.type}
              attachment={getAttachment(driver, doc.type)}
              disabled={busy || !driverLoaded}
              label={doc.label}
              onUpload={(file) => uploadDocument(doc.type, file)}
            >
              <div className="register-field-inline">
                <label className="register-field">
                  <span className="register-label">Issued on</span>
                  <input
                    className="register-input"
                    type="date"
                    value={fileDates[doc.type]?.issuedOn ?? ''}
                    onChange={(event) => updateDates(doc.type, 'issuedOn', event.target.value)}
                  />
                </label>
                <label className="register-field">
                  <span className="register-label">Valid to</span>
                  <input
                    className="register-input"
                    type="date"
                    value={fileDates[doc.type]?.validTo ?? ''}
                    onChange={(event) => updateDates(doc.type, 'validTo', event.target.value)}
                  />
                </label>
              </div>
            </UploadSlot>
          ))}
        </div>
      </AssistAccordion>

      <AssistAccordion
        badge={accountType === 'COMPANY_DRIVER' ? 'Company' : 'Owner'}
        copy="Choose the operating model, then capture company and banking details where applicable."
        title="Ownership and payout"
      >
        <div className="admin-assist-section-grid">
          <div className="register-field-group">
            <label className="register-field">
              <span className="register-label">Account type</span>
              <select
                className="register-input"
                value={accountType}
                onChange={(event) => setAccountType(event.target.value as 'INDIVIDUAL_OWNER' | 'COMPANY_DRIVER')}
              >
                <option value="INDIVIDUAL_OWNER">Individual owner</option>
                <option value="COMPANY_DRIVER">Company driver</option>
              </select>
            </label>
            <button
              className="register-secondary-button"
              disabled={busy || !driverLoaded}
              type="button"
              onClick={() => {
                const driverId = requireDriverId();
                if (driverId) void runDriverAction('Account type', () => updateAdminDriverOnboardingAccountType(driverId, accountType));
              }}
            >
              Save account type
            </button>
          </div>

          <form className="register-field-group" onSubmit={handleCompanySave}>
            <div className="register-field-inline">
              <label className="register-field">
                <span className="register-label">Company name</span>
                <input
                  className="register-input"
                  value={companyForm.companyName}
                  onChange={(event) => setCompanyForm((form) => ({ ...form, companyName: event.target.value }))}
                />
              </label>
              <label className="register-field">
                <span className="register-label">Company email</span>
                <input
                  className="register-input"
                  type="email"
                  value={companyForm.companyEmail}
                  onChange={(event) => setCompanyForm((form) => ({ ...form, companyEmail: event.target.value }))}
                />
              </label>
            </div>
            <div className="register-field-inline">
              <label className="register-field">
                <span className="register-label">Company phone</span>
                <input
                  className="register-input"
                  inputMode="tel"
                  value={companyForm.companyPhone1E164}
                  onChange={(event) => setCompanyForm((form) => ({ ...form, companyPhone1E164: event.target.value }))}
                />
              </label>
              <label className="register-field">
                <span className="register-label">Registration number</span>
                <input
                  className="register-input"
                  value={companyForm.companyRegNumber}
                  onChange={(event) => setCompanyForm((form) => ({ ...form, companyRegNumber: event.target.value }))}
                />
              </label>
            </div>
            <button className="register-secondary-button" disabled={busy || !driverLoaded} type="submit">
              Save company
            </button>
          </form>
        </div>

        <form className="register-field-group admin-assist-nested-block" onSubmit={handleBankSave}>
          <div className="register-field-inline">
            <label className="register-field">
              <span className="register-label">Bank</span>
              <input
                className="register-input"
                value={bankForm.bankName}
                onChange={(event) => setBankForm((form) => ({ ...form, bankName: event.target.value }))}
              />
            </label>
            <label className="register-field">
              <span className="register-label">Account number</span>
              <input
                className="register-input"
                value={bankForm.accountNumber}
                onChange={(event) => setBankForm((form) => ({ ...form, accountNumber: event.target.value }))}
              />
            </label>
          </div>
          <div className="register-field-inline">
            <label className="register-field">
              <span className="register-label">Branch code</span>
              <input
                className="register-input"
                value={bankForm.branchCode}
                onChange={(event) => setBankForm((form) => ({ ...form, branchCode: event.target.value }))}
              />
            </label>
            <label className="register-field">
              <span className="register-label">Account holder</span>
              <input
                className="register-input"
                value={bankForm.accountHolderName}
                onChange={(event) => setBankForm((form) => ({ ...form, accountHolderName: event.target.value }))}
              />
            </label>
          </div>
          <button className="register-secondary-button" disabled={busy || !driverLoaded} type="submit">
            Save bank details
          </button>
        </form>
      </AssistAccordion>

      <AssistAccordion
        badge="Finish"
        copy="Send the driver a password setup email if they were created by admin, then submit the profile for the normal approval queue."
        title="Password and review"
      >
        <div className="admin-assist-action-row">
          <button
            className="register-secondary-button"
            disabled={busy || !driverLoaded}
            type="button"
            onClick={() => {
              const driverId = requireDriverId();
              if (!driverId) return;
              void runMessageAction('Password setup email', async () => {
                const response = await sendAdminDriverPasswordSetup(driverId);
                return response.message;
              });
            }}
          >
            Send password setup
          </button>
          <button
            className="register-secondary-button"
            disabled={busy || !driverLoaded}
            type="button"
            onClick={() => {
              const driverId = requireDriverId();
              if (driverId) void runDriverAction('Review submission', () => submitAdminDriverOnboardingForReview(driverId));
            }}
          >
            Submit for review
          </button>
          {driver?.id ? (
            <Link className="admin-link-button" to={`/admin/drivers/${driver.id}`}>
              Open approval page
            </Link>
          ) : null}
        </div>
      </AssistAccordion>
    </AdminShell>
  );
};

export default AdminDriverOnboardingAssistPage;
